import { createFileRoute, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router"
import { useCallback } from "react"
import { z } from "zod"

import { arePermissionsDisabledForSession } from "@/helpers/orderedSteps/uploadFlags"
import { useOrderedSteps, useSessionDetail } from "@/services/dexie"
import { logger } from "@/utils/monitoring/logging"

import { BrowserPermissions } from "../../../../sections/permissions/BrowserPermissions"

export const Route = createFileRoute("/$sessionId/_session/permissions-guide/")({
  component: PermissionsGuideRoute,
  validateSearch: z.object({
    navigateBackOnDone: z.boolean().optional(),
    // If gps is mandatory (per customer configuration)
    gpsMandatory: z.boolean(),
  }),
})

function PermissionsGuideRoute() {
  const { sessionId } = Route.useParams()
  const { navigateBackOnDone, gpsMandatory } = Route.useSearch()
  const { history } = useRouter()
  const canGoBack = useCanGoBack()
  const navigate = useNavigate()

  const { data: orderedSteps, isLoading } = useOrderedSteps(sessionId)
  const { data: detail, isLoading: isDetailLoading } = useSessionDetail(sessionId)

  const onDoneCb = useCallback(() => {
    // When the page is opened directly via a shared link, there is no history to go
    // back to. Fallback to the session root which redirects to the first unfinished step.
    if (canGoBack) {
      logger.debug("[permissions-guide] onDoneCb: canGoBack=true → history.back()")
      history.back()
    } else {
      logger.debug("[permissions-guide] onDoneCb: canGoBack=false → navigate to /$sessionId")
      navigate({ to: "/$sessionId", params: { sessionId }, replace: true })
    }
  }, [canGoBack, history, navigate, sessionId])

  if (isLoading || !orderedSteps || isDetailLoading || !detail) {
    return null
  }

  return (
    <BrowserPermissions
      onDoneCb={navigateBackOnDone ? onDoneCb : undefined}
      gpsMandatory={gpsMandatory}
      permissionsDisabled={arePermissionsDisabledForSession(orderedSteps)}
      carSilhouetteEnabled={detail.carSilhouetteEnabled}
    />
  )
}
